/**
 * Brand Page - scroll reveal.
 *
 * Mirrors the Club/Wine Tasting reveal contract:
 * - IntersectionObserver owns viewport entry.
 * - GSAP owns transform/opacity motion only.
 * - Reduced motion, missing GSAP, or missing IntersectionObserver renders static.
 * - Theme editor section reloads can safely re-bind unrevealed elements.
 */
;(function () {
	window.__lBrandRevealActive = true

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	var revealObserver = null

	function initBrandReveal(root) {
		var els = getRevealTargets(root, ':not(.is-revealed)')

		if (!els.length) {
			if (!document.querySelector('[data-l-brand-reveal]:not(.is-revealed)')) {
				document.documentElement.classList.remove('l-brand-reveal-prep')
			}

			return
		}

		if (prefersReducedMotion || !window.gsap || !window.IntersectionObserver) {
			revealBrandStatic(root)

			return
		}

		if (revealObserver) revealObserver.disconnect()

		revealObserver = new IntersectionObserver(handleRevealEntries, {
			root: null,
			rootMargin: '0px 0px -12% 0px',
			threshold: 0.01,
		})

		els.forEach(function (el) {
			el.classList.add('is-reveal-bound')
			revealObserver.observe(el)
		})
	}

	function getRevealScope(root) {
		if (root && root.querySelectorAll) return root

		return document
	}

	function getRevealTargets(root, suffix) {
		var scope = getRevealScope(root)
		var selector = '[data-l-brand-reveal]' + (suffix || '')
		var els = Array.prototype.slice.call(scope.querySelectorAll(selector))

		if (scope.matches && scope.matches(selector)) {
			els.unshift(scope)
		}

		return els
	}

	function handleRevealEntries(entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) return

			revealObserver.unobserve(entry.target)
			try {
				playSectionReveal(entry.target)
			} catch (e) {
				markRevealComplete(entry.target)
			}
		})
	}

	function playSectionReveal(el) {
		if (el.classList.contains('is-revealed') || el.classList.contains('is-revealing')) return

		var config = getRevealConfig(el)
		var parts = getRevealParts(el)
		var media = getRevealMedia(el)
		var cta = config.cta ? getRevealCta(el) : []
		var animatedTargets = [el].concat(parts, media, cta)
		var tl = window.gsap.timeline({
			defaults: {
				ease: 'power3.out',
				overwrite: 'auto',
			},
			onComplete: function () {
				markRevealComplete(el)
				window.gsap.set(animatedTargets.length ? animatedTargets : el, {
					clearProps: 'opacity,visibility,transform,willChange',
				})
			},
		})

		el.classList.add('is-revealing')
		window.gsap.set(animatedTargets.length ? animatedTargets : el, { willChange: 'transform,opacity' })

		tl.fromTo(el, getRevealFrom(config.shell), getRevealTo(config.shell), 0)

		if (parts.length) {
			tl.fromTo(parts, getRevealFrom(config.parts), getRevealTo(config.parts), 0.08)
		}

		if (media.length) {
			tl.fromTo(media, getRevealFrom(config.media), getRevealTo(config.media), config.mediaAt)
		}

		if (cta.length) {
			tl.fromTo(cta, getRevealFrom(config.cta), getRevealTo(config.cta), config.ctaAt)
		}
	}

	function markRevealComplete(el) {
		el.classList.remove('is-reveal-bound')
		el.classList.remove('is-revealing')
		el.classList.add('is-revealed')

		if (!document.querySelector('[data-l-brand-reveal]:not(.is-revealed)')) {
			document.documentElement.classList.remove('l-brand-reveal-prep')
		}
	}

	function revealBrandStatic(root) {
		document.documentElement.classList.remove('l-brand-reveal-prep')
		getRevealTargets(root).forEach(function (el) {
			markRevealComplete(el)
		})
	}

	function getRevealFrom(vars) {
		var from = {}

		copyRevealProp(vars, from, 'x')
		copyRevealProp(vars, from, 'y')
		copyRevealProp(vars, from, 'scale')
		copyRevealProp(vars, from, 'transformOrigin')

		if (vars.opacity !== undefined) from.autoAlpha = vars.opacity

		return from
	}

	function getRevealTo(vars) {
		var to = {}

		copyRevealProp(vars, to, 'duration')
		copyRevealProp(vars, to, 'ease')
		copyRevealProp(vars, to, 'stagger')
		copyRevealProp(vars, to, 'transformOrigin')

		if (vars.opacity !== undefined) to.autoAlpha = 1
		if (vars.x !== undefined) to.x = 0
		if (vars.y !== undefined) to.y = 0
		if (vars.scale !== undefined) to.scale = 1

		return to
	}

	function copyRevealProp(source, target, prop) {
		if (source[prop] !== undefined) {
			target[prop] = source[prop]
		}
	}

	function getRevealConfig(el) {
		var config = {
			shell: {
				opacity: 0,
				y: 16,
				duration: 0.68,
				ease: 'power2.out',
			},
			parts: {
				opacity: 0,
				y: 14,
				scale: 0.99,
				duration: 0.72,
				stagger: { each: 0.045, from: 'start' },
			},
			media: {
				scale: 1.025,
				duration: 1,
				ease: 'power2.out',
				transformOrigin: '50% 55%',
			},
			mediaAt: 0.08,
		}

		if (el.classList.contains('l-brand-hero__inner')) {
			config.shell = {
				opacity: 0,
				scale: 0.985,
				duration: 0.78,
				ease: 'power2.out',
			}
			config.parts = {
				opacity: 0,
				y: -10,
				scale: 0.985,
				duration: 0.85,
				stagger: 0.06,
				transformOrigin: '50% 50%',
			}
			config.media.scale = 1.035
			config.media.duration = 1.2
			config.mediaAt = 0
		} else if (el.classList.contains('l-brand-card')) {
			config.parts = {
				opacity: 0,
				x: function (index, node) {
					return node.classList.contains('l-brand-card__media') ? -12 : 12
				},
				y: 0,
				scale: 0.99,
				duration: 0.76,
				stagger: { each: 0.055, from: 'start' },
			}
			config.cta = {
				opacity: 0,
				y: 8,
				duration: 0.4,
				ease: 'power2.out',
			}
			config.ctaAt = 0.3
			config.media.scale = 1.025
			config.mediaAt = 0.08
		}

		return config
	}

	function getRevealParts(el) {
		var selector

		if (el.classList.contains('l-brand-hero__inner')) {
			selector = '.l-brand-hero__title'
		} else if (el.classList.contains('l-brand-card')) {
			selector =
				'.l-brand-card__media, .l-brand-card__logo, .l-brand-card__name, .l-brand-card__location, .l-brand-card__description'
		}

		return selector ? getVisibleNodes(el.querySelectorAll(selector)) : []
	}

	function getRevealCta(el) {
		return getVisibleNodes(el.querySelectorAll('.l-brand-card__cta'))
	}

	function getRevealMedia(el) {
		return getVisibleNodes(
			el.querySelectorAll(
				'.l-brand-hero__media img, .l-brand-hero__media svg, .l-brand-hero__media video, .l-brand-card__media img, .l-brand-card__media svg'
			)
		)
	}

	function getVisibleNodes(nodes) {
		var seen = []

		return Array.prototype.filter.call(nodes, function (node) {
			if (!node || seen.indexOf(node) !== -1 || !node.getClientRects().length) return false
			seen.push(node)

			return true
		})
	}

	function safeInitBrandPage(root) {
		try {
			initBrandReveal(root)
		} catch (e) {
			revealBrandStatic(root)
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			safeInitBrandPage(document)
		})
	} else {
		safeInitBrandPage(document)
	}

	if (window.Shopify && window.Shopify.designMode) {
		document.addEventListener('shopify:section:load', function (event) {
			document.documentElement.classList.remove('l-brand-reveal-prep')
			safeInitBrandPage(event.target)
		})
	}
})()
